import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { initSkyCircuit } from "./sky-circuit";
import { BUILD_STEPS, assemblyState, glyphPose, interval, pointOnPath } from "./world-assembly.mjs";

/** Native-scroll journey: actual code glyphs descend into the same traced game geometry. */
export function initScrollWorld({ reduceMotion }) {
  const root = document.querySelector("[data-scroll-world]");
  if (!root) return () => {};
  const q = selector => root.querySelector(selector);
  const all = selector => Array.from(root.querySelectorAll(selector));
  let measureGeometry = () => {};
  const game = initSkyCircuit(root, () => measureGeometry());
  const editor = q("[data-world-editor]");
  const field = q("[data-world-field]");
  const viewport = q("[data-world-viewport]");
  const camera = q("[data-world-camera]");
  const glyphLayer = q("[data-world-labels]");
  const routeSvg = q("[data-world-streams]");
  const lines = all("[data-world-line]");
  const savedLines = lines.map(line => ({ line, html: line.innerHTML }));
  const removers = [], groups = [];
  let current = "", activeStep = -1, progress = 0, timeline, scrollTween, disposed = false;
  const cameraY = gsap.quickSetter(camera, "y", "px");
  const skyY = gsap.quickSetter(q("[data-world-background]"), "y", "px");
  const on = (element, name, fn) => { element.addEventListener(name, fn); removers.push(() => element.removeEventListener(name, fn)); };
  q("[data-world-announcement]").textContent = "";

  const setProgress = p => {
    progress = p;
    const state = assemblyState(p, field.clientHeight);
    root.dataset.progress = String(Math.round(p * 100));
    root.dataset.cameraY = String(Math.round(state.camera));
    cameraY(-state.camera);
    // The same sky layer drifts upward as the view travels down into the world.
    skyY(-Math.min(90, state.camera * 0.1));
    const intro = q("[data-world-intro]");
    const introText = p >= 0.13 && p < 0.89 ? lines[state.step].textContent : "An interactive development demo.";
    if (intro.textContent !== introText) intro.textContent = introText;
    if (state.step !== activeStep) {
      activeStep = state.step;
      const step = BUILD_STEPS[activeStep];
      root.dataset.buildObject = step.key;
      lines.forEach((line, index) => line.closest("code").classList.toggle("is-assembling", index === activeStep));
    }
    groups.forEach(group => {
      group.glyphs.forEach((glyph, index) => {
        const pose = glyphPose(p, group.step, index, group.glyphs.length, glyph.from, glyph.to, field.clientHeight);
        const flying = pose.started && p < 0.945;
        glyph.source.style.visibility = pose.started ? "hidden" : "";
        glyph.piece.style.visibility = flying ? "visible" : "hidden";
        glyph.piece.classList.toggle("is-landed", pose.landed);
        glyph.piece.style.transform = "translate3d(" + pose.x + "px," + pose.y + "px,0) rotate(" + pose.rotation + "deg) scale(" + pose.scale + ")";
      });
      const visible = p >= group.step.launch && p < group.step.traceEnd + 0.04;
      group.route.style.visibility = visible ? "visible" : "hidden";
      group.route.style.strokeDashoffset = String(group.length * (1 - interval(p, group.step.launch, group.step.traceEnd)));
    });
    game.drawProgress(p);
    if (state.stage === current) return;
    current = state.stage;
    root.dataset.stage = current;
    const play = current === "play";
    all("[data-world-stage]").forEach(element => {
      if (element.dataset.worldStage === current) element.setAttribute("aria-current", "step");
      else element.removeAttribute("aria-current");
    });
    q("[data-world-hud]").hidden = !play;
    q("[data-world-controls]").hidden = !play;
    q("[data-world-skip]").hidden = play;
    q("[data-world-rebuild]").hidden = !play;
    q("[data-world-overlay]").inert = !play;
    editor.inert = p >= 0.13;
    editor.setAttribute("aria-hidden", String(p >= 0.13));
    game.setEnabled(play);
  };

  if (reduceMotion) {
    gsap.set(editor, { autoAlpha: 0 });
    gsap.set(q("[data-world-background]"), { opacity: 1 });
    setProgress(1);
  } else {
    lines.forEach((line, lineIndex) => {
      line.closest("code").setAttribute("role", "group");
      line.closest("code").setAttribute("aria-label", line.textContent);
      line.setAttribute("aria-hidden", "true");
      const step = BUILD_STEPS[lineIndex];
      const group = { step, glyphs: [], route: document.createElementNS("http://www.w3.org/2000/svg", "path"), length: 1 };
      routeSvg.appendChild(group.route);
      const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(node => {
        const fragment = document.createDocumentFragment();
        Array.from(node.textContent).forEach(character => {
          const source = document.createElement("span");
          source.className = "scroll-world__source-glyph";
          source.textContent = character;
          fragment.appendChild(source);
          if (/\s/.test(character)) return;
          const piece = document.createElement("span");
          piece.className = "scroll-world__flying-glyph";
          piece.textContent = character;
          glyphLayer.appendChild(piece);
          group.glyphs.push({ source, piece, from: { x: 0, y: 0 }, to: { x: 0, y: 0, angle: 0 } });
        });
        node.replaceWith(fragment);
      });
      groups.push(group);
    });
    const measure = () => {
      if (disposed || !field.clientWidth) return;
      const bounds = field.getBoundingClientRect();
      const actualCamera = -Number(gsap.getProperty(camera, "y")) || 0;
      const paths = game.constructionPaths();
      routeSvg.setAttribute("viewBox", "0 0 " + field.clientWidth + " " + field.clientHeight * 2.4);
      groups.forEach(group => {
        group.glyphs.forEach((glyph, index) => {
          const rect = glyph.source.getBoundingClientRect();
          glyph.from = { x: rect.left - bounds.left, y: rect.top - bounds.top + actualCamera };
          // Once the editor has passed overhead, subsequent instructions enter
          // from the top of the travelling view, directly below the active code.
          if (group.step.key !== "ground") glyph.from.y = assemblyState(group.step.launch, field.clientHeight).camera + 8;
          const contour = group.step.key === "energy" ? paths.energyCopies[index % 3] : paths[group.step.key];
          const fraction = group.step.key === "energy" ? Math.floor(index / 3) / Math.ceil(group.glyphs.length / 3) : index / Math.max(1, group.glyphs.length);
          glyph.to = pointOnPath(contour, fraction);
          glyph.piece.style.font = getComputedStyle(glyph.source).font;
          glyph.piece.style.color = getComputedStyle(glyph.source).color;
        });
        const from = group.glyphs[Math.floor(group.glyphs.length / 2)].from;
        const to = pointOnPath(paths[group.step.key], 0.5);
        group.route.setAttribute("d", "M" + from.x + " " + from.y + " C" + from.x + " " + (from.y + field.clientHeight * 0.42) + " " + to.x + " " + (to.y - field.clientHeight * 0.27) + " " + to.x + " " + to.y);
        group.length = group.route.getTotalLength() || 1;
        group.route.style.strokeDasharray = String(group.length);
      });
      setProgress(progress);
    };
    measureGeometry = measure;
    measure();
    const durationAnchor = { value: 0 };
    timeline = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        id: "scroll-world-build", trigger: root, pin: viewport,
        start: "top 88px",
        end: () => "+=" + Math.max(2500, Math.round(window.innerHeight * 3.8)),
        scrub: true, invalidateOnRefresh: true, onRefreshInit: measure,
        onUpdate: self => setProgress(self.progress),
        onRefresh: self => setProgress(self.progress),
      },
    });
    timeline.addLabel("code", 0).addLabel("descent", 0.13).addLabel("robot", 0.32)
      .addLabel("energy", 0.49).addLabel("portal", 0.66).addLabel("material", 0.89).addLabel("play", 0.985);
    timeline.to(durationAnchor, { value: 1, duration: 1 }, 0);
    lines.forEach((line, i) => {
      timeline.fromTo(line, { clipPath: i === 0 ? "inset(0 0 0 0)" : "inset(0 100% 0 0)" }, {
        clipPath: "inset(0 0% 0 0)", duration: 0.025, ease: "steps(" + line.textContent.length + ")",
      }, i * 0.022);
    });
    // The editor physically leaves through the top with the camera. Its opacity
    // stays 1; the characters themselves travel into the objects below.
    timeline.to(q("[data-world-scroll-hint]"), { autoAlpha: 0, duration: 0.04 }, 0.13)
      .fromTo(q("[data-world-background]"), { opacity: 0 }, { opacity: 1, duration: 0.08 }, 0.89);
    setProgress(timeline.scrollTrigger.progress);
  }
  const navigate = end => {
    game.pause();
    if (reduceMotion) {
      gsap.set(editor, { autoAlpha: end ? 0 : 1 });
      cameraY(0);
      setProgress(end ? 1 : 0);
      q(end ? "[data-world-start]" : "[data-world-skip]").focus({ preventScroll: true });
      return;
    }
    scrollTween?.kill();
    scrollTween = gsap.to(window, {
      scrollTo: { y: end ? timeline.scrollTrigger.end : timeline.scrollTrigger.start, autoKill: true },
      duration: 0.9, ease: "power2.inOut",
      onComplete: () => q(end ? "[data-world-start]" : "[data-world-skip]").focus({ preventScroll: true }),
    });
  };
  on(q("[data-world-skip]"), "click", () => navigate(true));
  on(q("[data-world-rebuild]"), "click", () => navigate(false));
  document.fonts?.ready.then(() => { if (!disposed) ScrollTrigger.refresh(); });
  return () => {
    disposed = true; measureGeometry = () => {};
    scrollTween?.kill(); timeline?.scrollTrigger?.kill(); timeline?.kill();
    removers.forEach(remove => remove());
    groups.forEach(group => { group.glyphs.forEach(glyph => glyph.piece.remove()); group.route.remove(); });
    savedLines.forEach(({ line, html }) => {
      line.innerHTML = html; line.removeAttribute("aria-hidden");
      const row = line.closest("code"); row.classList.remove("is-assembling"); row.removeAttribute("role"); row.removeAttribute("aria-label");
    });
    game.destroy();
  };
}
